<i18n>
{
  "en": {
    "verificationCode": "Verification code (5 digits)",
    "kookaburra": {
      "login": {
        "otp": {
          "auto": {
            "lead": "Verification code is read. Please verify."
          },
          "sms": {
            "lead": "Verification code has been sent by text message to {0}."
          },
          "ivr": {
            "lead": "You will receive a call to {0}. A verification code will be read to you (5 digits)"
          }
        },
        "abort": "Abort"
      },
      "errors": {
        "wrongotp": "Wrong verification code.",
        "toomanyotpattempts": "There has been too many attempts.",
        "wrongformatotp": "Given verification code is not valid. Only numbers are allowed.",
        "otpnotfound": "Could not find the requested verification code.",
        "requestnotvalid": "Request is not valid",
        "missingcookie": "Missing cookie"
      }
    }
  },
  "no": {
    "verificationCode": "Bekreftelseskode (5 siffer)",
    "kookaburra": {
      "login": {
        "otp": {
          "auto": {
            "lead": "Bekreftelseskode er lest. Vennligst bekreft."
          },
          "sms": {
            "lead": "Bekreftelseskode sendt på SMS til {0}."
          },
          "ivr": {
            "lead": "Du blir oppringt på {0}. Bekreftelseskoden leses opp (5 siffer)."
          }
        },
        "abort": "Avbryt"
      },
      "errors": {
        "wrongotp": "Feil bekreftelseskode.",
        "toomanyotpattempts": "Du har prøvd for mange ganger.",
        "wrongformatotp": "Bekreftelseskoden har feil format. Kun tall er lov.",
        "otpnotfound": "Ingen gyldig sesjon",
        "requestnotvalid": "Sesjonen er ikke gyldig lenger",
        "missingcookie": "Mangler informasjonskapsel"
      }
    }
  }
}
</i18n>

<template>
  <FormWrapper>
    <div class="page-md">
      <TnHeading type="h1" size="s" align="center" class="margin-vertical-2xl">
        {{ t(`kookaburra.login.otp.${ivrOrSms}.lead`, [phoneNumber]) }}
      </TnHeading>

      <TnNotificationInline
        v-if="error"
        horizontal
        status="critical"
        full-width
        class="margin-bottom-l"
      >
        {{ t(error.i18nPath) }}
      </TnNotificationInline>

      <form ref="formEl" :action="loginUrl" method="post" class="margin-top-l">
        <TnTextField
          v-model="otp"
          name="otp"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="5"
          pattern="[0-9]*"
          :label="t('verificationCode')"
        />

        <TnButton
          type="submit"
          primary
          block
          class="margin-top-l"
          icon-right="cta-right"
          :disabled="disabledButton"
        />

        <input v-if="route.query.ivr" type="hidden" name="ivr" :value="route.query.ivr" />
      </form>
    </div>
  </FormWrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  TnButton,
  TnHeading,
  TnNotificationInline,
  TnTextField,
} from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import { ErrorService } from '@/error/error.service';

const { t } = useI18n();
const route = useRoute();

const formEl = useTemplateRef<HTMLFormElement>('formEl');
const otp = ref<string>((route.query.otp_from as string) ?? '');

const disabledButton = computed(() => !otp.value || otp.value.length === 0);

const ivrOrSms = computed(() => {
  if (route.query.otp_from) return 'auto';
  return route.query.ivr === 'true' ? 'ivr' : 'sms';
});

const loginUrl = computed(() => `${import.meta.env.VITE_KOOKABURRA_BASE_URL}/oauth/login`);

const phoneNumber = computed(() => route.query.phoneNumber);

const error = computed(() =>
  route.query.error ? ErrorService.getError('kookaburra', route.query.error) : null,
);

/** WebOTP: auto-fill the SMS code and submit when the browser supports it. */
onMounted(() => {
  if (!('OTPCredential' in window)) return;

  const controller = new AbortController();
  formEl.value?.addEventListener('submit', () => controller.abort());

  navigator.credentials
    .get({ otp: { transport: ['sms'] }, signal: controller.signal } as CredentialRequestOptions)
    .then((credential) => {
      const code = (credential as { code?: string } | null)?.code;
      if (!code) return;

      otp.value = code;
      formEl.value?.submit();
    })
    .catch((err) => console.log(err));
});
</script>
